import { useState, useEffect, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  Area,
  ComposedChart,
} from 'recharts';
import { predictionApi } from '@/api/prediction';
import type {
  ForecastResponse,
  TrendAnalysisResponse,
  SeasonalityResponse,
} from '@/types/data';
import { useAuthStore } from '@/stores/authStore';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  Activity,
  Info,
} from 'lucide-react';

type ViewMode = 'forecast' | 'trend' | 'seasonality';

const METRIC_OPTIONS = [
  { value: 'daily_value', label: '日次登録数' },
];

export default function Prediction() {
  const { accessibleCompanies } = useAuthStore();
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('forecast');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // パラメータ
  const [metric, setMetric] = useState('daily_value');
  const [trainingDays, setTrainingDays] = useState(30);
  const [forecastDays, setForecastDays] = useState(14);
  const [periodDays, setPeriodDays] = useState(30);

  // データ
  const [forecastData, setForecastData] = useState<ForecastResponse | null>(null);
  const [trendData, setTrendData] = useState<TrendAnalysisResponse | null>(null);
  const [seasonalityData, setSeasonalityData] = useState<SeasonalityResponse | null>(null);

  // 初期化
  useEffect(() => {
    if (accessibleCompanies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(accessibleCompanies[0].id);
    }
  }, [accessibleCompanies, selectedCompanyId]);

  // 予測データ取得
  const fetchForecast = useCallback(async () => {
    if (!selectedCompanyId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await predictionApi.getForecast({
        company_id: selectedCompanyId,
        metric,
        training_days: trainingDays,
        forecast_days: forecastDays,
      });
      setForecastData(data);
    } catch (err) {
      console.error('予測データ取得エラー:', err);
      setError('予測データの取得に失敗しました。データが不足している可能性があります。');
    } finally {
      setIsLoading(false);
    }
  }, [selectedCompanyId, metric, trainingDays, forecastDays]);

  // トレンドデータ取得
  const fetchTrend = useCallback(async () => {
    if (!selectedCompanyId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await predictionApi.getTrend({
        company_id: selectedCompanyId,
        metric,
        period_days: periodDays,
      });
      setTrendData(data);
    } catch (err) {
      console.error('トレンドデータ取得エラー:', err);
      setError('トレンドデータの取得に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  }, [selectedCompanyId, metric, periodDays]);

  // 季節性データ取得
  const fetchSeasonality = useCallback(async () => {
    if (!selectedCompanyId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await predictionApi.getSeasonality({
        company_id: selectedCompanyId,
        metric,
        period_days: Math.max(60, periodDays),
      });
      setSeasonalityData(data);
    } catch (err) {
      console.error('季節性データ取得エラー:', err);
      setError('季節性データの取得に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  }, [selectedCompanyId, metric, periodDays]);

  // ビューモード変更時にデータ取得
  useEffect(() => {
    if (!selectedCompanyId) return;

    if (viewMode === 'forecast') {
      fetchForecast();
    } else if (viewMode === 'trend') {
      fetchTrend();
    } else if (viewMode === 'seasonality') {
      fetchSeasonality();
    }
  }, [viewMode, selectedCompanyId, fetchForecast, fetchTrend, fetchSeasonality]);

  // トレンド方向アイコン
  const getTrendIcon = (direction: string) => {
    switch (direction) {
      case 'up':
        return <TrendingUp className="w-5 h-5 text-green-600" />;
      case 'down':
        return <TrendingDown className="w-5 h-5 text-red-600" />;
      default:
        return <Minus className="w-5 h-5 text-gray-600" />;
    }
  };

  // トレンド方向テキスト
  const getTrendText = (direction: string) => {
    switch (direction) {
      case 'up':
        return '上昇傾向';
      case 'down':
        return '下降傾向';
      default:
        return '横ばい';
    }
  };

  // 予測チャート用データ
  const getForecastChartData = () => {
    if (!forecastData) return [];

    const historicalData = forecastData.historical.map((h) => ({
      date: h.date.slice(5), // MM-DD
      actual: h.value,
      predicted: null as number | null,
      lower: null as number | null,
      upper: null as number | null,
    }));

    const predictionData = forecastData.predictions.map((p) => ({
      date: p.date.slice(5),
      actual: null as number | null,
      predicted: p.value,
      lower: p.lower,
      upper: p.upper,
    }));

    return [...historicalData, ...predictionData];
  };

  // トレンドチャート用データ
  const getTrendChartData = () => {
    if (!trendData) return [];

    return (trendData.data ?? []).map((d, i) => ({
      date: (d.date ?? '').slice(5),
      value: d.value ?? 0,
      ma7: trendData.moving_averages?.[i]?.value ?? null,
    }));
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6">予測分析</h1>

      {/* フィルター */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">法人</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={selectedCompanyId || ''}
              onChange={(e) => setSelectedCompanyId(Number(e.target.value))}
            >
              <option value="">選択してください</option>
              {accessibleCompanies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.display_name || company.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">指標</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={metric}
              onChange={(e) => setMetric(e.target.value)}
            >
              {METRIC_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          {viewMode === 'forecast' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  学習期間（日）
                </label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={trainingDays}
                  onChange={(e) => setTrainingDays(Number(e.target.value))}
                >
                  <option value={14}>14日</option>
                  <option value={30}>30日</option>
                  <option value={60}>60日</option>
                  <option value={90}>90日</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  予測期間（日）
                </label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={forecastDays}
                  onChange={(e) => setForecastDays(Number(e.target.value))}
                >
                  <option value={7}>7日</option>
                  <option value={14}>14日</option>
                  <option value={30}>30日</option>
                </select>
              </div>
            </>
          )}
          {(viewMode === 'trend' || viewMode === 'seasonality') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                分析期間（日）
              </label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={periodDays}
                onChange={(e) => setPeriodDays(Number(e.target.value))}
              >
                <option value={30}>30日</option>
                <option value={60}>60日</option>
                <option value={90}>90日</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* タブ切り替え */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          className={`px-4 py-2 font-medium flex items-center gap-2 ${
            viewMode === 'forecast'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setViewMode('forecast')}
        >
          <Activity className="w-4 h-4" />
          時系列予測
        </button>
        <button
          className={`px-4 py-2 font-medium flex items-center gap-2 ${
            viewMode === 'trend'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setViewMode('trend')}
        >
          <TrendingUp className="w-4 h-4" />
          トレンド分析
        </button>
        <button
          className={`px-4 py-2 font-medium flex items-center gap-2 ${
            viewMode === 'seasonality'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setViewMode('seasonality')}
        >
          <Calendar className="w-4 h-4" />
          季節性分析
        </button>
      </div>

      {/* エラー表示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* ローディング */}
      {isLoading && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">分析中...</p>
        </div>
      )}

      {/* 予測ビュー */}
      {!isLoading && viewMode === 'forecast' && forecastData && (
        <div className="space-y-6">
          {/* サマリーカード */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500 mb-1">予測モデル</div>
              <div className="text-lg font-semibold">線形回帰</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500 mb-1">決定係数 (R²)</div>
              <div className="text-lg font-semibold">
                {forecastData.accuracy_metrics?.r_squared?.toFixed(3) || '-'}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500 mb-1">平均絶対誤差</div>
              <div className="text-lg font-semibold">
                {forecastData.accuracy_metrics?.mae?.toFixed(1) || '-'}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500 mb-1">7日後予測値</div>
              <div className="text-lg font-semibold text-blue-600">
                {forecastData.predictions[6]?.value?.toFixed(0) || '-'}
              </div>
            </div>
          </div>

          {/* 予測チャート */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">予測グラフ（信頼区間付き）</h2>
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={getForecastChartData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="upper"
                    stroke="none"
                    fill="#93c5fd"
                    fillOpacity={0.3}
                    name="信頼区間上限"
                  />
                  <Area
                    type="monotone"
                    dataKey="lower"
                    stroke="none"
                    fill="#ffffff"
                    name="信頼区間下限"
                  />
                  <Line
                    type="monotone"
                    dataKey="actual"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name="実績値"
                  />
                  <Line
                    type="monotone"
                    dataKey="predicted"
                    stroke="#dc2626"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ r: 3 }}
                    name="予測値"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 flex items-start gap-2 text-sm text-gray-500">
              <Info className="w-4 h-4 mt-0.5" />
              <span>
                青色の実線は過去の実績値、赤色の破線は予測値を示します。
                薄い青色の領域は95%信頼区間です。
              </span>
            </div>
          </div>
        </div>
      )}

      {/* トレンドビュー */}
      {!isLoading && viewMode === 'trend' && trendData && (
        <div className="space-y-6">
          {/* トレンドサマリー */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500 mb-1">トレンド方向</div>
              <div className="flex items-center gap-2">
                {getTrendIcon(trendData.trend?.direction ?? 'stable')}
                <span className="text-lg font-semibold">
                  {getTrendText(trendData.trend?.direction ?? 'stable')}
                </span>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500 mb-1">日次変化率</div>
              <div
                className={`text-lg font-semibold ${
                  (trendData.trend?.slope_percent ?? 0) > 0
                    ? 'text-green-600'
                    : (trendData.trend?.slope_percent ?? 0) < 0
                    ? 'text-red-600'
                    : ''
                }`}
              >
                {(trendData.trend?.slope_percent ?? 0) > 0 ? '+' : ''}
                {(trendData.trend?.slope_percent ?? 0).toFixed(2)}%/日
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500 mb-1">期間平均</div>
              <div className="text-lg font-semibold">
                {(trendData.trend?.avg_value ?? 0).toFixed(1)}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500 mb-1">ボラティリティ</div>
              <div className="text-lg font-semibold">
                {(trendData.trend?.volatility ?? 0).toFixed(1)}
              </div>
            </div>
          </div>

          {/* トレンドチャート */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">トレンドグラフ（7日移動平均付き）</h2>
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={getTrendChartData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#2563eb"
                    strokeWidth={1}
                    dot={{ r: 2 }}
                    name="日次値"
                  />
                  <Line
                    type="monotone"
                    dataKey="ma7"
                    stroke="#dc2626"
                    strokeWidth={2}
                    dot={false}
                    name="7日移動平均"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 統計情報 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">統計情報</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-sm text-gray-500">最小値</div>
                <div className="text-xl font-semibold">{(trendData.trend?.min_value ?? 0).toFixed(1)}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">最大値</div>
                <div className="text-xl font-semibold">{(trendData.trend?.max_value ?? 0).toFixed(1)}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">決定係数 (R²)</div>
                <div className="text-xl font-semibold">{(trendData.trend?.r_squared ?? 0).toFixed(3)}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">傾き</div>
                <div className="text-xl font-semibold">
                  {(trendData.trend?.slope ?? 0) > 0 ? '+' : ''}
                  {(trendData.trend?.slope ?? 0).toFixed(2)}/日
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 季節性ビュー */}
      {!isLoading && viewMode === 'seasonality' && seasonalityData && (
        <div className="space-y-6">
          {/* 季節性サマリー */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500 mb-1">最も活発な曜日</div>
              <div className="text-lg font-semibold text-green-600">
                {seasonalityData.best_day ?? '-'}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500 mb-1">最も低調な曜日</div>
              <div className="text-lg font-semibold text-red-600">
                {seasonalityData.worst_day ?? '-'}
              </div>
            </div>
          </div>

          {/* 曜日別チャート */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">曜日別パターン</h2>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={seasonalityData.day_of_week_pattern ?? []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Bar
                    dataKey="avg_value"
                    fill="#3b82f6"
                    name="平均値"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 曜日別インデックス */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">曜日別指数（平均=100）</h2>
            <div className="grid grid-cols-7 gap-2">
              {(seasonalityData.day_of_week_pattern ?? []).map((pattern) => (
                <div
                  key={pattern.day}
                  className={`p-3 rounded-lg text-center ${
                    (pattern.index ?? 100) >= 110
                      ? 'bg-green-100 text-green-800'
                      : (pattern.index ?? 100) <= 90
                      ? 'bg-red-100 text-red-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  <div className="text-xs font-medium">{(pattern.day ?? '').slice(0, 1)}</div>
                  <div className="text-lg font-bold">{(pattern.index ?? 100).toFixed(0)}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-start gap-2 text-sm text-gray-500">
              <Info className="w-4 h-4 mt-0.5" />
              <span>
                指数が100より大きい曜日は平均より活発、100より小さい曜日は平均より低調です。
              </span>
            </div>
          </div>
        </div>
      )}

      {/* データなし */}
      {!isLoading && !error && !forecastData && !trendData && !seasonalityData && (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          法人を選択してください
        </div>
      )}
    </div>
  );
}
